import React from 'react';
import isDarkColor from 'is-dark-color';
import Link from 'src/components/atoms/Link';

import AvatarGroup from 'src/components/molecules/AvatarGroup';
import CircleValue from 'src/components/atoms/CircleValue';
import CardBadge from 'src/components/atoms/CardBadge';
import useMediaQuery from 'src/hooks/useMediaQuery';
import { sizes } from 'src/constants/screenSizes';

import styles from './landingChallengeCard.module.scss';
const {
  main,
  titleText,
  prizeText,
  participantsWrapper,
  circleValue,
  challengeImage,
  cardBottomWrapper,
  cardFooter,
  container,
  cardBadgeWrapper,
  organizerName,
  organizerLogo,
  organizerWrapper,
  logoWrapper,
} = styles;

import { Users } from 'src/types';
import Tooltip from 'src/components/atoms/Tooltip';

const SHORTHAND_HEX = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
const FULL_HEX = /^#?[a-f\d]{6}$/i;

/**
 * Challenge#banner_color accepts both `#RGB` and `#RRGGBB`, but is-dark-color
 * only parses the six-digit form: its hexToRgb returns null for anything else
 * and isDarkColor then throws destructuring that null. Screen the input rather
 * than letting a colour the model considers valid take down the page.
 */
const isDarkBanner = (color: string): boolean => {
  if (typeof color !== 'string') {
    return false;
  }

  const shorthand = color.match(SHORTHAND_HEX);
  const hex = shorthand ? `#${shorthand.slice(1, 4).map(digit => digit + digit).join('')}` : color;

  // An unreadable colour reads as light, so the card falls back to dark text.
  return FULL_HEX.test(hex) ? isDarkColor(hex) : false;
};

export type LandingChallengeCardProps = {
  image: string;
  name: string;
  prize: [string];
  users: [Users];
  color: string;
  loading: boolean;
  badgeColor: string;
  challengeEndDate: string;
  cardBadge: boolean;
  organizers: [{ name: string; logo: string; link: string }];
  userCount: number;
  url: string;
  priority?: boolean;
};

const LandingChallengeCard = ({
  image,
  name,
  prize,
  users,
  color,
  loading,
  badgeColor,
  challengeEndDate,
  cardBadge,
  organizers,
  userCount,
  url,
  priority,
}: LandingChallengeCardProps) => {
  const { wide, xLarge, xSmall, small, medium, large, smallMedium } = sizes;
  const isS = useMediaQuery(small);

  // The masthead slots come from the feature_challenge_1/2/3 flags, which default
  // to false — with none set the controller sends null and MastHeadLanding spreads
  // it, delivering every prop here as undefined. Skip the card rather than
  // dereferencing them. Placed after the hooks so hook order stays stable.
  if (!name && !url) {
    return null;
  }

  const isWhite = color === '#FFFFFF';

  // Detects the dark color
  const isDark = isDarkBanner(color);
  const invertedColor = isDark ? '#ffffff' : '#1f1f1f';

  return (
    <>
      <div className={container}>
        <a href={url}>
          <div className={main} style={{ background: color }}>
            <div className={cardBadgeWrapper}>
              <CardBadge badgeColor={badgeColor} challengeEndDate={challengeEndDate} cardBadge={cardBadge} />
            </div>
            <img style={{ display: 'block' }}
              src={image}
              width={isS ? 112 : 180}
              height={isS ? 112 : 180}
              className={challengeImage}
            />
            <div className={cardBottomWrapper}>
              <div className={titleText} style={{ color: invertedColor }}>
                {name}
              </div>
              <div className={prizeText} style={{ color: isWhite ? '#747474' : invertedColor }}>
                {/* {prize} */}
                {prize.map((priz, i) => {
                  return (
                    <span key={i} style={{ color: isWhite ? '#747474' : invertedColor }}>
                      {priz} {i + 1 < prize.length && '•'}&nbsp;
                    </span>
                  );
                })}
              </div>
            </div>

            <div className={participantsWrapper}>
              <AvatarGroup
                users={users}
                size="sm"
                onCard={true}
                borderColor={color}
                loading={loading}
                priority={priority}
              />
              <div className={circleValue}>
                <CircleValue value={userCount} size="sm" onCard={true} borderColor={color} />
              </div>
            </div>
          </div>
        </a>
        <div className={cardFooter}>
          {organizers.map(organizer => {
            const { name, logo, link } = organizer;
            return (
              <a href={link} key={name}>
                <div className={organizerWrapper}>
                  <Tooltip position="down" label={name}>
                    <div className={logoWrapper}>
                      <img style={{ display: 'block' }}
                        src={logo}
                      width="100%"/>
                    </div>
                  </Tooltip>
                  {/* Hide names if organizers are more then 1 */}
                  {organizers?.length < 2 ? (
                    <Tooltip position="down" label={name}>
                      <div className={organizerName}>{name}</div>{' '}
                    </Tooltip>
                  ) : (
                    <></>
                  )}
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default LandingChallengeCard;
